import { Maximize2, Video } from 'lucide-react'

import {
  AttachmentType,
  SupportRequestAttachment,
} from '@/app/entities/SupportRequest'
import { cn } from '@/app/utils/cn'
import { formatHistoryDate } from '@/app/utils/supportRequest'
import { withCdn } from '@/app/utils/withCdn'
import { LinkifiedText } from '@/view/components/LinkifiedText'

import { formatFileSize } from '../useSupportRequestDetailSheet'

interface ChatMessageBubbleProps {
  isOwn: boolean
  authorLabel: string
  showAuthor: boolean
  text: string
  date: string
  attachments: SupportRequestAttachment[]
  onOpenAttachment: (attachment: SupportRequestAttachment) => void
}

export const ChatMessageBubble = ({
  isOwn,
  authorLabel,
  showAuthor,
  text,
  date,
  attachments,
  onOpenAttachment,
}: ChatMessageBubbleProps) => (
  <div
    className={cn(
      'flex w-full flex-col',
      isOwn ? 'items-end' : 'items-start',
      showAuthor ? 'mt-2' : 'mt-0.5',
    )}
  >
    <div
      className={cn(
        'flex max-w-[85%] flex-col gap-1 rounded-2xl px-3 py-2',
        isOwn
          ? 'rounded-br-sm bg-primary text-white'
          : 'rounded-bl-sm bg-gray-100 text-black03',
      )}
    >
      {showAuthor && (
        <span
          className={cn(
            'text-[11px] font-semibold',
            isOwn ? 'text-white/90' : 'text-black04',
          )}
        >
          {authorLabel}
        </span>
      )}

      {!!text && (
        <p className="select-text whitespace-pre-wrap break-words text-xs">
          <LinkifiedText
            text={text}
            linkClassName={cn(
              'underline',
              isOwn ? 'text-white' : 'text-primary',
            )}
          />
        </p>
      )}

      {!!attachments.length && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <button
              key={attachment.url}
              type="button"
              onClick={() => onOpenAttachment(attachment)}
              title={attachment.name}
              className={cn(
                'relative overflow-hidden rounded-lg border text-left',
                isOwn ? 'border-white/40' : 'border-gray-300',
              )}
            >
              {attachment.type === AttachmentType.VIDEO ? (
                <span
                  className={cn(
                    'flex max-w-[200px] items-center gap-2 px-2 py-2',
                    isOwn ? 'bg-white/15' : 'bg-white',
                  )}
                >
                  <Video className="h-6 w-6 shrink-0" />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-[11px] font-semibold">
                      {attachment.name}
                    </span>
                    <span className="text-[10px] opacity-80">
                      {formatFileSize(attachment.size)}
                    </span>
                  </span>
                </span>
              ) : (
                <>
                  <img
                    src={withCdn(attachment.url)}
                    alt={attachment.name}
                    className="h-24 w-24 object-cover"
                  />
                  <Maximize2 className="absolute bottom-1 right-1 h-3.5 w-3.5 rounded bg-black/50 p-0.5 text-white" />
                </>
              )}
            </button>
          ))}
        </div>
      )}

      <span
        className={cn(
          'self-end text-[10px]',
          isOwn ? 'text-white/80' : 'text-black04',
        )}
      >
        {formatHistoryDate(date)}
      </span>
    </div>
  </div>
)
